import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { EventFilter } from '@/pages/dashboard/filterPageComponents/eventAnalysis/EventExplorationFilter/components/EventFilter';
import { useSelector } from 'umi';
import Filter from '@/pages/dashboard/components/NewFilter';
import CommonBubble from '@home/components/CommonBubble';
import classNames from 'classnames';
import { FORM_MAP } from '@/pages/dashboard/pages/overview/_config/constant';
import { setBtnPointLog } from "@/pages/dashboard/utils/utils";
import { getDateDefault } from '@/pages/dashboard/utils/dateUtils';
import { formatDateFromFilterValue } from "@/pages/dashboard/utils/formatDate";
import { PC_CONSOLE_DEFAULT_FILTER_CONFIG } from "@/pages/dashboard/components/NewFilter/_config";
import { useLocal } from '@/pages/dashboard/utils/customHook';
import './index.scss';
import { setCustomFilterList } from '@/pages/dashboard/components/NewFilter/customFilterList';

import { IEventFilterValue } from './components/EventFilter/components/EventFilterContent/type';
import { IEventExplorationFilterProps, IInputListItem, IFilterValue, IEventCustomFilterValue, IDimensionOption } from '@/pages/dashboard/filterPageComponents/eventAnalysis/EventExplorationFilter/type';
import { ISeasonInfo } from '@/pages/dashboard/types/dashboard';
import { generateEventFilterTitle } from '@/pages/dashboard/filterPageComponents/eventAnalysis/EventExplorationFilter/utils';
import { CUSTOM_VALUE } from './components/EventFilter/components/EventFilterContent/config';
import { EVENT_PARAM_OPERATOR_MAP } from './config';
import { useEventExplorationFilter } from './hooks';
import useEventExplorationSelector from './useEventExplorationSelector';
import DimensionFilter, { DIMENSION_ALL_VALUE } from './components/DimensionFilter';
import { getLanguage } from '@home/utils/common';
import { NUMERIC_CHILD_SUFFIX } from '@/pages/datalab/pages/ABExperiment/components/EventFilter/components/EventPanel/utils';

const FILTER_PATH = 'eventAnalysis/eventExploration';

const DEFAULT_EVENT_FILTER_VALUE: IEventFilterValue = {
  eventName: '',
  eventParameter: {
    type: 'market',
    regions: [],
    markets: [],
  },
  hoursSinceRegister: '',
  hoursSinceRegisterCustom: '',
  minValue: '',
  maxValue: '',
};

const EventExplorationFilter = (props: IEventExplorationFilterProps) => {
  const { filterConfirm, filterReset, filterInit, buttonLogConfig, openSticky = false, path = FILTER_PATH, children, isHaveFilterSave, timeZone = 0, config } = props;
  const filterValueRef = useRef<IFilterValue | null>(null);
  const { getLocalText } = useLocal('filter');
  const {
    api: selectorApi = '/api/v1/dashboard/pc/event/exploration/select',
    extraSelectorParams,
    haveEventFilter = true,
    haveDimensionFilter = true,
    isEventFilterRequired = true,
    hoursSinceRegisterOptions,
    compEventNameSelect,
    dimensionOptions: propsDimensionOptions,
    defaultDimensionValue = [],
    eventErrorTipsEn = 'Please filter the event first, then view the data.',
    eventErrorTipsCn = '请先筛选事件，再查看数据。',
  } = config || {};

  // 调用外部接口获取筛选器下拉数据（使用专用 hook，不污染全局 selector）
  const { selectorData, selectorLoading } = useEventExplorationSelector({
    selectorApi,
    extraSelectorParams
  });

  const { isEN: isENRaw, locale } = useLocal();
  const isEN = useMemo(() => isENRaw || locale?.startsWith('en'), [isENRaw, locale]);

  const seasonInfo = useSelector((state: { dashboardSetting?: { gameConfig?: { season_info?: ISeasonInfo; }; }; }) =>
    state?.dashboardSetting?.gameConfig?.season_info || { date: [], week: [], month: [] },
  );

  const [inputList] = useState<IInputListItem[]>([
    {
      type: 'rangeDaily',
      key: 'rangeTime',
      default: getDateDefault({ timeType: 'day', timeZone, startNum: 1, endNum: 1 }),
      extraShortcut: seasonInfo
    },
  ]);

  const [eventFilterValue, setEventFilterValue] = useState<IEventFilterValue>(DEFAULT_EVENT_FILTER_VALUE);
  const [showEventFilterError, setShowEventFilterError] = useState(false);

  const [dimensionValue, setDimensionValue] = useState<string[]>(defaultDimensionValue);

  const dimensionOptions = useMemo(() => {
    return propsDimensionOptions?.map((item: any) => {
      return {
        ...item,
        text: getLanguage(isEN, item?.textEn || '', item?.textCn || ''),
      };
    }) || [];
  }, [isEN, propsDimensionOptions]);

  // 统一处理维度取值：当选择 all 时，等价为所有维度值都被选中
  const getNormalizedDimensions = useCallback((dimensionValueProps?: string[]): string[] => {
    const dV = dimensionValueProps ?? dimensionValue;
    if (!haveDimensionFilter) {
      return [];
    }
    if (dV?.length === 1 && dV?.[0] === DIMENSION_ALL_VALUE) {
      return (dimensionOptions || []).map((item: IDimensionOption) => item.value);
    }
    return dV;
  }, [haveDimensionFilter, dimensionValue, dimensionOptions]);

  // 默认情况下，将事件相关的筛选器 key 加入到自定义筛选器列表中
  useEffect(() => {
    if (haveEventFilter) {
      setCustomFilterList(['event_name', 'event_params', 'hours_since_register', 'pv_threshold']);
    }
  }, [haveEventFilter]);

  const {
    getEventNameOptions,
    getEventParametersOptions,
  } = useEventExplorationFilter({
    isEN,
    data: selectorData?.event_list || [],
    eventFilterValue,
  });

  const getCustomFilters = useCallback((eventVal: IEventFilterValue): IEventCustomFilterValue => {
    if (!haveEventFilter) {
      return {};
    }
    const filters: IEventCustomFilterValue = {
      event_name: [],
      event_params: [],
      hours_since_register: [],
      pv_threshold: [],
    };
    // 1. event_name
    if (eventVal.eventName) {
      filters.event_name = [eventVal.eventName];

      // 2. event_params
      const options = getEventParametersOptions;

      const eventParam = eventVal.eventParameter;
      if (eventParam?.markets?.length > 0) {
        const valuesMap: Record<string, string[]> = {};
        const operatorsMap: Record<string, string> = {};

        options.forEach((paramOption) => {
          const paramKey = paramOption.value;
          const selectedChildren = eventParam.markets
            .filter((market: string) => {
              if (market.endsWith(NUMERIC_CHILD_SUFFIX)) return false;
              return paramOption.children?.some(c => c.value === market);
            })
            .map((market: string) => market.split('@__@')[1] || market);

          if (selectedChildren.length > 0) {
            valuesMap[paramKey] = selectedChildren;
            operatorsMap[paramKey] = 'in';
          }
        });

        // 处理 numeric/text 类型参数
        const numericConditions = eventParam.numeric || {};
        Object.entries(numericConditions).forEach(([paramKey, cond]) => {
          const numericMarketValue = `${paramKey}${NUMERIC_CHILD_SUFFIX}`;
          if (!eventParam.markets?.includes(numericMarketValue)) return;
          if (!cond.operator || !cond.value?.some((v: string) => v !== '')) return;
          valuesMap[paramKey] = cond.value;
          operatorsMap[paramKey] = EVENT_PARAM_OPERATOR_MAP[cond.operator] ?? cond.operator;
        });

        if (Object.keys(valuesMap).length > 0) {
          filters.event_params = [JSON.stringify(valuesMap), JSON.stringify(operatorsMap)];
        }
      }
    }

    // 3. hours_since_register
    const hours = eventVal.hoursSinceRegister === CUSTOM_VALUE
      ? eventVal.hoursSinceRegisterCustom
      : eventVal.hoursSinceRegister;
    if (hours) {
      filters.hours_since_register = [hours];
    }

    // 4. pv_threshold
    if (eventVal.minValue || eventVal.maxValue) {
      filters.pv_threshold = [eventVal.minValue || '', eventVal.maxValue || ''];
    }

    return filters;
  }, [haveEventFilter, getEventParametersOptions]);

  const onConfirm = useCallback((value: IFilterValue) => {
    // 当启用事件筛选器时，校验事件名称、注册时长、事件触发次数是否为空
    if (haveEventFilter) {
      const hours = eventFilterValue.hoursSinceRegister === CUSTOM_VALUE
        ? eventFilterValue.hoursSinceRegisterCustom
        : eventFilterValue.hoursSinceRegister;
      const hasEventName = !!eventFilterValue.eventName;
      const hasHours = !!hours;
      const hasEventOccurCount = !!eventFilterValue.maxValue;
      if (!hasEventName || !hasHours || !hasEventOccurCount) {
        setShowEventFilterError(true);
        return;
      }
    }
    setShowEventFilterError(false);
    const normalizedDimensions = getNormalizedDimensions();
    filterValueRef.current = {
      ...value,
      dimension: normalizedDimensions,
    };
    // setFormValue(filterValueRef.current);
    const obj = {
      ...(formatDateFromFilterValue(value) || {}),
      [FORM_MAP.RANGE_TIME_DIFF]: false,
      timeType: "Day",
      ...getCustomFilters(eventFilterValue),
      ...(haveDimensionFilter ? { dimensions: normalizedDimensions } : {}),
      // eventFilter: eventFilterValue,
    };
    filterConfirm(obj);
    setBtnPointLog(buttonLogConfig, formatDateFromFilterValue(value));
  }, [filterConfirm, buttonLogConfig, eventFilterValue, getCustomFilters, haveDimensionFilter, haveEventFilter, getNormalizedDimensions]);

  const onInit = useCallback((value: IFilterValue, _inputList: IInputListItem[], isHaveExtendFIlter: boolean) => {
    let newDimensionValue: string[] | undefined;
    // 如果从已保存的筛选器中恢复，优先使用其中的 dimension 字段初始化维度
    if (haveDimensionFilter && Array.isArray(value?.dimension)) {
      newDimensionValue = value?.dimension;
      setDimensionValue(newDimensionValue);
    }
    const normalizedDimensions = getNormalizedDimensions(newDimensionValue);
    filterValueRef.current = {
      ...value,
      dimension: normalizedDimensions,
    };
    // setFormValue(filterValueRef.current);
    filterInit({
      ...(formatDateFromFilterValue(value) || {}),
      [FORM_MAP.RANGE_TIME_DIFF]: !isHaveExtendFIlter,
      timeType: "Day",
      rangeDateKey: 'rangeDaily',
      ...getCustomFilters(eventFilterValue),
      ...(haveDimensionFilter ? { dimensions: normalizedDimensions } : {}),
      // eventFilter: eventFilterValue,
    });
  }, [filterInit, eventFilterValue, getCustomFilters, haveDimensionFilter, getNormalizedDimensions]);

  const onReset = useCallback((value: IFilterValue) => {
    setShowEventFilterError(false);
    // 重置时维度恢复为默认配置 defaultDimensionValue
    setEventFilterValue(DEFAULT_EVENT_FILTER_VALUE);
    setDimensionValue(defaultDimensionValue);

    // 计算默认维度对应的实际维度值（支持默认就是 all 的情况）
    let defaultDimensions: string[] = [];
    if (haveDimensionFilter) {
      if (
        defaultDimensionValue.length === 1 &&
        defaultDimensionValue[0] === DIMENSION_ALL_VALUE
      ) {
        defaultDimensions = (dimensionOptions || []).map(
          (item: IDimensionOption) => item.value,
        );
      } else {
        defaultDimensions = defaultDimensionValue;
      }
    }

    filterValueRef.current = {
      ...value,
      dimension: defaultDimensions,
    };
    // setFormValue(filterValueRef.current);

    const obj = {
      ...(formatDateFromFilterValue(value) || {}),
      [FORM_MAP.RANGE_TIME_DIFF]: true,
      timeType: 'Day',
      ...getCustomFilters(DEFAULT_EVENT_FILTER_VALUE),
      ...(haveDimensionFilter ? { dimensions: defaultDimensions } : {}),
      // eventFilter: DEFAULT_EVENT_FILTER_VALUE,
    };
    filterReset(obj);
    setBtnPointLog(buttonLogConfig, formatDateFromFilterValue(value, 'Day'), true);
  }, [filterReset, buttonLogConfig, getCustomFilters, haveDimensionFilter, defaultDimensionValue, dimensionOptions]);

  const handleEventFilterConfirm = useCallback((value: IEventFilterValue) => {
    setEventFilterValue(value);
    setShowEventFilterError(false);
  }, []);

  const eventErrorTips = getLanguage(isEN, eventErrorTipsEn, eventErrorTipsCn);

  const computedEventParameterOptions = useMemo(() => {
    const eventList = selectorData?.event_list || [];
    if (eventList.length > 0 && eventFilterValue.eventName) {
      return getEventParametersOptions;
    }
    return [];
  }, [selectorData?.event_list, eventFilterValue.eventName, getEventParametersOptions]);

  const triggerTitle = useMemo(() => {
    return generateEventFilterTitle({
      value: eventFilterValue,
      eventNameOptions: getEventNameOptions,
      eventParameterOptions: computedEventParameterOptions,
      getLocalText,
      isEn: isEN,
    });
  }, [eventFilterValue, getEventNameOptions, computedEventParameterOptions, getLocalText, isEN]);

  const extraRenderContent = (
    <>
      {haveEventFilter && (
        <div className="dbd-event-exploration-filter__group">
          <span className={classNames('dbd-event-exploration-filter__label', {
            'dbd-event-exploration-filter__label--required': isEventFilterRequired,
          })}>{getLocalText('event_filter')}</span>
          <div className={classNames('dbd-event-exploration-filter__content', {
            'dbd-event-exploration-filter__content--error': showEventFilterError,
          })}>
            <CommonBubble
              className="dbd-event-exploration-filter__bubble"
              content={showEventFilterError ? eventErrorTips : ''}
              overlayClassName="dbd-event-exploration-filter__bubble-box"
              visible={showEventFilterError}
              placement="top"
              escapeWithReference
              flipped={false}
              destroyOnClose={false}
            >
              <span className="dbd-event-exploration-filter__trigger-wrap">
                <EventFilter
                  value={eventFilterValue}
                  triggerTitle={triggerTitle}
                  hoursSinceRegisterOptions={hoursSinceRegisterOptions}
                  data={selectorData?.event_list || []}
                  config={{compEventNameSelect}}
                  onConfirm={handleEventFilterConfirm}
                  onCancel={() => {}}
                />
              </span>
            </CommonBubble>
          </div>
        </div>
      )}
      {haveDimensionFilter && (
        <div className="dbd-event-exploration-filter__group dbd-event-exploration-filter__group--dimension">
          <span className="dbd-event-exploration-filter__label">{getLocalText('dimension_filter')}</span>
          <div className="dbd-event-exploration-filter__content">
            <DimensionFilter
              value={dimensionValue}
              options={dimensionOptions}
              onChange={setDimensionValue}
            />
          </div>
        </div>
      )}
    </>
  );

  const filterStyle: Record<string, string> = {
    '--general-filter-label': `'${getLocalText('general_filter')}'`,
  };
  
  const hasExtraContent = haveEventFilter || haveDimensionFilter;

  return <div className="dbd-event-exploration-filter" style={filterStyle}>
    <Filter
      filterHideDefaultIsReq={true}
      pageDefaultSelector={PC_CONSOLE_DEFAULT_FILTER_CONFIG}
      openSticky={openSticky}
      path={path}
      latestDateType={''}
      value={filterValueRef.current}
      inputList={inputList}
      onInit={onInit}
      onConfirm={onConfirm}
      onReset={onReset}
      isHaveFilterSave={isHaveFilterSave}
      extraRenderContent={hasExtraContent ? extraRenderContent : undefined}
      // 外部传入selectorApi使用其他selector数据源
      {...(selectorApi ? {
        outSelector: selectorData,
        loading: selectorLoading,
      } : {})}
    >
      {children}
    </Filter>
  </div>;
};

export default EventExplorationFilter;
